import { useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useStoryStore } from '../../store/storyStore';

/**
 * SceneRenderer component
 * Renders the current scene's background (video or image)
 */
export function SceneRenderer() {
  const { currentScene, currentNarrative, gameState } = useStoryStore((state) => ({ 
    currentScene: state.currentScene, 
    currentNarrative: state.currentNarrative,
    gameState: state.gameState
  }));

  // Generate a randomized camera drift for the current scene
  const cameraDrift = useMemo(() => {
    const isTense = currentNarrative?.type === 'Action' || (gameState['suspicion'] as number > 40);
    const duration = isTense ? 30 : 60; // Slower for calm, faster for tension
    
    return {
      scale: [1.0, 1.08],
      x: [Math.random() * 2 - 1, Math.random() * -15 - 5], // Drift left/right
      y: [Math.random() * 2 - 1, Math.random() * 10 - 5],  // Drift up/down
      duration
    };
  }, [currentScene?.id]);

  if (!currentScene) {
    return (
      <div className="absolute inset-0 bg-gradient-to-br from-dark-900 via-dark-800 to-dark-900" />
    );
  }

  const hasVideo = currentScene.videoUrl && currentScene.videoUrl.length > 0;
  const hasImage = currentScene.backgroundImageUrl && currentScene.backgroundImageUrl.length > 0;
  
  // Determine if background should be blurred (when a narrative overlay image is present)
  const shouldBlur = !!currentNarrative?.image;

  return (
    <div className="scene-renderer absolute inset-0 overflow-hidden bg-black">
      <AnimatePresence mode="wait">
        <motion.div
          key={currentScene.id}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 1.5 }}
          className="absolute inset-0"
        >
            {/* Background Container with Blur Transition */}
            <div className={`absolute inset-0 transition-all duration-1000 ${shouldBlur ? 'blur-[12px] scale-110 brightness-[0.5]' : 'blur-0 scale-100 brightness-100'}`}>
                {/* Video Background */}
                {hasVideo && (
                  <video
                    src={currentScene.videoUrl}
                    className="absolute inset-0 w-full h-full object-cover"
                    autoPlay
                    loop
                    muted
                    playsInline
                  />
                )}

                {/* Image Background with Organic Ken Burns Effect */}
                {!hasVideo && hasImage && (
                  <motion.div
                    className="absolute inset-0 w-full h-full bg-cover bg-center"
                    style={{ backgroundImage: `url(${currentScene.backgroundImageUrl})` }}
                    animate={{
                      scale: cameraDrift.scale,
                      x: cameraDrift.x,
                      y: cameraDrift.y
                    }}
                    transition={{
                      duration: cameraDrift.duration,
                      repeat: Infinity,
                      repeatType: "reverse",
                      ease: "easeInOut"
                    }}
                  />
                )}

                {/* Fallback Gradient */}
                {!hasVideo && !hasImage && (
                  <div className="absolute inset-0 bg-gradient-to-br from-gray-900 to-black" />
                )}
            </div>

            {/* Subtle Gradient Overlay for Text Readability */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-black/20 pointer-events-none" />
          </motion.div>
      </AnimatePresence>
    </div>
  );
}
